import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { LazyImage } from '../components/LazyMedia';
import './Dashboard.css';
import { useCurrentWorkout } from '../contexts/WorkoutContext';

const getExercisePreviewFromEntity = (exerciseEntity) => {
  if (!exerciseEntity) {
    return '';
  }
  if (exerciseEntity.previewUrl) {
    return exerciseEntity.previewUrl;
  }
  if (exerciseEntity.previewImageS3Key && exerciseEntity.id) {
    return `/media/exercises/${exerciseEntity.id}/preview`;
  }
  if (exerciseEntity.previewImageS3Url) {
    return exerciseEntity.previewImageS3Url;
  }
  if (exerciseEntity.previewImageUrl) {
    return exerciseEntity.previewImageUrl;
  }
  return '';
};

const getWorkoutPreviewUrl = (workout) => {
  if (!workout) {
    return '';
  }
  if (workout.previewUrl) {
    return workout.previewUrl;
  }
  const firstExerciseLink = Array.isArray(workout.exercises) ? workout.exercises[0] : undefined;
  if (!firstExerciseLink) {
    return '';
  }
  if (firstExerciseLink.previewUrl) {
    return firstExerciseLink.previewUrl;
  }
  const exerciseEntity = firstExerciseLink.exercise ?? firstExerciseLink;
  return getExercisePreviewFromEntity(exerciseEntity);
};

const getWorkoutExerciseCount = (workout) => {
  if (!workout) {
    return 0;
  }
  if (typeof workout.exerciseCount === 'number') {
    return workout.exerciseCount;
  }
  if (Array.isArray(workout.exercises)) {
    return workout.exercises.length;
  }
  return 0;
};

const Dashboard = () => {
  const { workout, workoutProgress, loading } = useCurrentWorkout();

  const hasActiveWorkout = useMemo(
    () => Boolean(workoutProgress && !workoutProgress.completed),
    [workoutProgress]
  );
  const workoutExerciseCount = useMemo(() => getWorkoutExerciseCount(workout), [workout]);
  const workoutPreviewUrl = useMemo(() => getWorkoutPreviewUrl(workout), [workout]);

  // Helper function for proper word declension
  const getExerciseWord = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
      return 'упражнений';
    }

    if (lastDigit === 1) {
      return 'упражнение';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'упражнения';
    }

    return 'упражнений';
  };

  // Generate gradient based on workout number
  const getWorkoutGradient = (workoutNumber) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%)',
      'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
      'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    ];
    return gradients[(workoutNumber - 1) % gradients.length];
  };

  const quickActions = [
    { title: 'AI Тренер AIRNI', icon: '🤖', link: '/app/coach', color: '#7c3aed' },
    { title: 'Мой прогресс', icon: '📊', link: '/app/progress', color: '#0891b2' },
    { title: 'Профиль', icon: '👤', link: '/app/profile', color: '#059669' },
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Главная</h1>
        <p>Продолжайте тренировки для достижения результата</p>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Today's Workout */}
        <div className="workout-card main-card">
          <div className="card-header">
            <h3>Твоя текущая тренировка</h3>
            <span className="workout-date">
              {new Date().toLocaleDateString('ru-RU', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </span>
          </div>

          {workout ? (
            <>
              <Link to={`/app/workouts/${workout.id}`} className="workout-preview-link">
                <div className="workout-preview">
                  <div
                    className="workout-image-placeholder"
                    style={{
                      background: workoutPreviewUrl
                        ? '#000'
                        : getWorkoutGradient(workout.workoutNumber),
                      position: 'relative'
                    }}
                  >
                    {workoutPreviewUrl && (
                      <LazyImage
                        src={resolveMediaUrl(workoutPreviewUrl)}
                        alt={workout.name || 'Превью тренировки'}
                        className="workout-image"
                        rootMargin={300}
                        style={{
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    )}
                    {workoutExerciseCount > 0 && (
                      <span className="dashboard-exercise-count">
                        {workoutExerciseCount} {getExerciseWord(workoutExerciseCount)}
                      </span>
                    )}
                  </div>
                </div>
              </Link>

              <div className="workout-info">
                <h4>{workout.name}</h4>
                <p className="workout-description">{workout.description}</p>
                <div className="workout-meta">
                  <span className="meta-item">
                    <span className="meta-icon">📚</span>
                    {workoutExerciseCount} {getExerciseWord(workoutExerciseCount)}
                  </span>
                </div>
              </div>

              <Link to={`/app/workouts/${workout.id}`} className="btn-primary btn-full">
                {hasActiveWorkout ? 'Продолжить Тренировку' : 'Начать Тренировку'}
              </Link>

            </>
          ) : (
            <div className="no-workout">
              <p>🎉 Вы завершили все доступные тренировки!</p>
              <p>Отличная работа! Продолжайте в том же духе.</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h3>Быстрые действия</h3>
          <div className="actions-grid">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.link}
                className="action-card"
                style={{ '--action-color': action.color }}
              >
                <div className="action-icon">{action.icon}</div>
                <div className="action-title">{action.title}</div>
              </Link>
            ))}
          </div>

          {/* Info Card */}
          <div className="info-card">
            <h4>📋 Как это работает?</h4>
            <ul>
              <li>Тренировки открываются последовательно</li>
              <li>Завершите все упражнения в тренировке</li>
              <li>После завершения откроется следующая</li>
              <li>Отслеживайте прогресс в разделе "Мой прогресс"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
